// Graphique Cashflow avec Recharts

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { CashflowPoint } from '../../types/dashboard';

interface CashflowChartProps {
  data: CashflowPoint[];
}

export const CashflowChart: React.FC<CashflowChartProps> = ({ data }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Formater les dates du format "2025-02" vers "Fév 25"
  const formatMois = (mois: string) => {
    const [annee, moisNum] = mois.split('-');
    const moisNoms = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    return `${moisNoms[parseInt(moisNum) - 1]} ${annee.slice(2)}`;
  };

  // Transformer les données pour le graphique
  const chartData = data.map((item) => ({
    mois: formatMois(item.mois),
    reel: item.reel,
    previsionnel: item.previsionnel,
  }));

  const formatMontant = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}k€`;
    }
    return `${value}€`;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 1.5,
            boxShadow: theme.shadows[3],
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {payload[0].payload.mois}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box
              key={index}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  borderRadius: '50%',
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {entry.name}:
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {entry.value.toLocaleString('fr-FR')} €
              </Typography>
            </Box>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Card elevation={2}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
          Cashflow - Réalisé vs Prévisionnel
        </Typography>

        <ResponsiveContainer width="100%" height={isMobile ? 250 : 350}>
          <BarChart
            data={chartData}
            margin={{
              top: 5,
              right: isMobile ? 5 : 30,
              left: isMobile ? 0 : 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis
              dataKey="mois"
              tick={{ fontSize: isMobile ? 11 : 12 }}
              stroke={theme.palette.text.secondary}
            />
            <YAxis
              tickFormatter={formatMontant}
              tick={{ fontSize: isMobile ? 11 : 12 }}
              stroke={theme.palette.text.secondary}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: isMobile ? 12 : 14 }}
              iconType="circle"
            />
            <Bar
              dataKey="reel"
              name="Réel"
              fill={theme.palette.success.main}
              radius={[8, 8, 0, 0]}
            />
            <Bar
              dataKey="previsionnel"
              name="Prévisionnel"
              fill={theme.palette.info.main}
              radius={[8, 8, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
